import { faSearch } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import useNonInitialEffect from '@versiondos/hooks';
import clsx from 'clsx';
import { Spinner } from 'components/UI';
import Button from 'components/UI/V3/Button/Button';
import { Input } from 'components/UI/V3/Forms/Input/Input';
import { useApiClientWrapper } from 'hooks';
import { FunctionComponent, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import NoPromocodes from './NoPromocodes';
import PromocodeItem from './PromocodeItem';
import styles from './PromocodesSelector.module.scss';
import SearchNotFound from './SearchNotFound';
import AlertMessage from 'components/UI/V3/AlertMessage';
import { faCircleExclamation } from '@fortawesome/pro-solid-svg-icons/faCircleExclamation';
import {
    getInvoiceThunk,
    addPromocodeToInvoiceThunk,
    fetchPromocodesForInvoiceThunk,
    InvoicesStatus
} from '@spike/invoices-action';
import Invoice, { InvoicePromocode } from '@spike/invoice-model';

interface PromocodesSelectorProps {
    invoice: Invoice;
    className?: string;
    onAdded?: () => void;
    onClose?: () => void;
}

export const PromocodesSelector: FunctionComponent<PromocodesSelectorProps> = props => {
    const dispatch = useDispatch();
    const apiClientWrapper = useApiClientWrapper();

    const promocodes = useSelector<RootState, Array<InvoicePromocode>>(state => state.invoices.promocodes.list);
    const promocodeErrorMessage = useSelector<RootState, string>(state => state.invoices.promocodes.errorMessage);
    const invoicesStatus = useSelector<RootState, InvoicesStatus>(state => state.invoices.status);

    const [searchText, setSearchText] = useState('');
    const [loading, setLoading] = useState({ promocodes: true, saving: false });
    const [selectedPromocodeId, setSelectedPromocodeId] = useState<number | null>(null);
    const [errorMessage, setErrorMessage] = useState('');

    useEffect(() => {
        props.invoice.id && dispatch(fetchPromocodesForInvoiceThunk(apiClientWrapper, props.invoice.id));
    }, []);

    useNonInitialEffect(() => {
        switch (invoicesStatus) {
            case InvoicesStatus.FetchPromocodesSuccess:
                setLoading(prev => ({ ...prev, promocodes: false }));
                break;
            case InvoicesStatus.AddPromocodeToInvoiceSuccess:
                props.invoice.id && dispatch(getInvoiceThunk(apiClientWrapper, props.invoice.id));
                break;
            case InvoicesStatus.AddPromocodeToInvoiceError:
                setErrorMessage(promocodeErrorMessage);
                setLoading(prev => ({ ...prev, saving: false }));
                break;
            case InvoicesStatus.GetInvoiceSuccess:
                setLoading(prev => ({ ...prev, saving: false }));
                props.onAdded?.();
                break;
            case InvoicesStatus.Error:
                setLoading(prev => ({ ...prev, promocodes: false, saving: false }));
                props.onClose?.();
        }
    }, [invoicesStatus]);

    const invoicePromocodeIds = props.invoice.promocodes.map(promocode => promocode.id);
    const filteredPromocodes = promocodes.filter(
        promocode =>
            !invoicePromocodeIds.includes(promocode.id) &&
            (searchText.trim().length === 0 || promocode.name.toLowerCase().indexOf(searchText.toLowerCase()) > -1)
    );

    const applyHandler = () => {
        if (selectedPromocodeId !== null && props.invoice.id) {
            setErrorMessage('');
            setLoading(prev => ({ ...prev, saving: true }));
            dispatch(addPromocodeToInvoiceThunk(apiClientWrapper, props.invoice.id, selectedPromocodeId));
        }
    };

    return (
        <div className={clsx(styles.container, props.className)}>
            {promocodes.length > 0 && (
                <div className={styles.header}>
                    {errorMessage.trim().length > 0 && (
                        <AlertMessage
                            icon={faCircleExclamation}
                            bdColor="#EF4F57"
                            bgColor="#FBB9BC33"
                            iconColor="#EF4F57"
                            className={styles.alertMessage}
                        >
                            {errorMessage}
                        </AlertMessage>
                    )}
                    <Input
                        placeholder="Search by promo"
                        className={styles.searchInput}
                        value={searchText}
                        onChange={(event: React.ChangeEvent<HTMLInputElement>) => setSearchText(event.target.value)}
                        startIcon={
                            <FontAwesomeIcon
                                icon={faSearch}
                                className={styles.searchIcon}
                            />
                        }
                        disabled={loading.saving}
                        autoFocus={false}
                    />
                </div>
            )}
            <div className={styles.body}>
                {loading.promocodes && (
                    <div className={styles.spinnerContainer}>
                        <Spinner />
                    </div>
                )}
                {!loading.promocodes &&
                    promocodes.length > 0 &&
                    filteredPromocodes.length > 0 &&
                    filteredPromocodes.map(promocode => (
                        <PromocodeItem
                            key={promocode.id}
                            promocode={promocode}
                            selected={promocode.id === selectedPromocodeId}
                            disabled={loading.saving}
                            onClick={() => setSelectedPromocodeId(promocode.id)}
                        />
                    ))}
                {!loading.promocodes && promocodes.length > 0 && filteredPromocodes.length === 0 && <SearchNotFound />}
                {!loading.promocodes && promocodes.length === 0 && <NoPromocodes />}
            </div>
            {promocodes.length > 0 && (
                <div className={styles.footer}>
                    <Button
                        label="Apply"
                        variant="black"
                        disabled={loading.promocodes || selectedPromocodeId === null}
                        isLoading={loading.saving}
                        fullWidth={true}
                        onClick={applyHandler}
                    />
                </div>
            )}
        </div>
    );
};

export default PromocodesSelector;
